﻿/**
 * Form controls — DESIGN.md §5: large padding, surface-container-lowest fill,
 * focus shifts to surface-bright with a "ghost border" of primary at 20%.
 * Heavy 2px outlines are forbidden.
 */

const CONTROL =
  "w-full rounded-xl border-none bg-surface-container-lowest px-4 py-3 text-base sm:text-sm text-on-surface placeholder:text-on-surface-variant/50 transition-all duration-200 focus:bg-surface-bright focus:ring-2 focus:ring-primary/20 outline-none disabled:opacity-60";

export function Field({ label, hint, error, required, htmlFor, children }) {
  return (
    <div className="space-y-1.5">
      {label && (
        <label
          htmlFor={htmlFor}
          className="block text-xs font-bold uppercase tracking-wider text-on-surface-variant"
        >
          {label}
          {required && <span className="ml-1 text-error">*</span>}
        </label>
      )}
      {children}
      {error ? (
        <p className="text-xs font-medium text-error">{error}</p>
      ) : (
        hint && <p className="text-xs text-on-surface-variant/70">{hint}</p>
      )}
    </div>
  );
}

export function Input({ className = "", invalid, ...rest }) {
  return (
    <input
      className={`${CONTROL} ${invalid ? "ring-2 ring-error/30" : ""} ${className}`}
      aria-invalid={invalid || undefined}
      {...rest}
    />
  );
}

export function Textarea({ className = "", rows = 3, ...rest }) {
  return <textarea rows={rows} className={`${CONTROL} ${className}`} {...rest} />;
}

export function Select({ className = "", invalid, children, ...rest }) {
  return (
    <div className="relative">
      <select
        className={`${CONTROL} cursor-pointer appearance-none pr-10 ${
          invalid ? "ring-2 ring-error/30" : ""
        } ${className}`}
        aria-invalid={invalid || undefined}
        {...rest}
      >
        {children}
      </select>
      <span className="material-symbols-outlined pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-base text-on-surface-variant">
        expand_more
      </span>
    </div>
  );
}

export function SearchInput({ className = "", ...rest }) {
  return (
    <div className={`relative ${className}`}>
      <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-lg text-on-surface-variant/60">
        search
      </span>
      <input type="search" className={`${CONTROL} pl-11`} {...rest} />
    </div>
  );
}

/** Multi-select rendered as toggle chips — used for the appointment service picker. */
export function ChipMultiSelect({ options = [], value = [], onChange, emptyHint }) {
  const selected = new Set(value);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange([...next]);
  };

  if (!options.length) {
    return (
      <p className="rounded-xl bg-surface-container-low px-4 py-3 text-sm text-on-surface-variant">
        {emptyHint ?? "Nothing to choose from yet."}
      </p>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const active = selected.has(option.id);
        return (
          <button
            key={option.id}
            type="button"
            onClick={() => toggle(option.id)}
            aria-pressed={active}
            className={`min-h-[48px] rounded-xl px-4 py-2 text-xs font-bold uppercase tracking-wide transition-all duration-200 active:scale-[0.97] ${
              active
                ? "gold-gradient text-on-primary shadow-gold-lg"
                : "bg-surface-container text-on-surface-variant hover:bg-surface-container-highest hover:text-on-surface"
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export default Input;
